import React, { CSSProperties } from "react";
import { Spinner, SpinnerSize } from "@fluentui/react";
import { ChatItem } from "./ChatItem";
import { getThemeColors, Theme } from "../../theme/colors";

type Chat = {
  id: string;
  question: string;
  display_name: string;
  notes: string;
  thumbs: boolean | null;
  feedback: string;
  is_public: boolean | null;
  latest_created_at: string | number | Date;
  created_at: string | number | Date;
  is_shared: boolean;
};

interface ChatHistoryProps {
  isLoading: boolean;
  chatList: Chat[];
  sharedChatList: Chat[];
  sharedWithUserChats: Chat[];
  username: string;
  colorMode: string;
  renamingChatId: string | null;
  newDisplayName: string;
  setNewDisplayName: (name: string) => void;
  handleRenameSubmit: (e: React.FormEvent) => void;
  handleRenameCancel: () => void;
  onChatCreate: (id: string, shared: boolean) => void;
  handleOptionsClick: (
    e: React.MouseEvent,
    chatId: string,
    isShared: boolean
  ) => void;
}

export const ChatHistory: React.FC<ChatHistoryProps> = ({
  isLoading,
  chatList,
  sharedChatList,
  sharedWithUserChats,
  username,
  colorMode,
  renamingChatId,
  newDisplayName,
  setNewDisplayName,
  handleRenameSubmit,
  handleRenameCancel,
  onChatCreate,
  handleOptionsClick,
}) => {
  const currentColors = getThemeColors(username, colorMode as Theme);

  const chatHistoryWrapperStyle: CSSProperties = {
    height: "calc(100vh - 380px)",
    overflowY: "auto",
    position: "relative",
  };

  const chatHistoryStyle: CSSProperties = {
    display: "flex",
    flexDirection: "column",
    gap: "0.5rem",
  };

  const renderChatList = (
    chats: Chat[],
    noOptions: boolean,
    passedInShared: boolean,
    title?: string
  ) => (
    <>
      {title && (
        <div
          style={{
            padding: "0.5rem 0.25rem",
            fontSize: "0.875rem",
            fontWeight: "600",
            color: currentColors.text,
            opacity: 0.7,
            textTransform: "uppercase",
            letterSpacing: "0.05em",
          }}
        >
          {title}
        </div>
      )}
      {chats.map((chat) => (
        <ChatItem
          key={chat.id}
          chat={chat}
          isRenaming={renamingChatId === chat.id}
          newDisplayName={newDisplayName}
          setNewDisplayName={setNewDisplayName}
          handleRenameSubmit={handleRenameSubmit}
          handleRenameCancel={handleRenameCancel}
          onChatCreate={onChatCreate}
          handleOptionsClick={handleOptionsClick}
          noOptions={noOptions}
          passedInShared={passedInShared}
          username={username}
          colorMode={colorMode}
        />
      ))}
    </>
  );

  return (
    <>
      <div style={chatHistoryWrapperStyle}>
        {isLoading ? (
          <Spinner
            size={SpinnerSize.small}
            label="Thinking"
            styles={{
              circle: {
                borderTopColor: currentColors.text,
                borderRightColor: "rgba(59, 145, 97, 0.3)",
                borderBottomColor: "rgba(59, 145, 97, 0.3)",
                borderLeftColor: "rgba(59, 145, 97, 0.3)",
              },
              label: { color: currentColors.text },
            }}
          />
        ) : (
          <div style={chatHistoryStyle}>
            {chatList.length > 0 &&
              renderChatList(
                chatList,
                false,
                false,
                chatList.length > 0 && sharedChatList.length > 0
                  ? "Chats"
                  : undefined
              )}
            {sharedChatList.length > 0 && (
              <>
                {sharedWithUserChats.length > 0 && (
                  <div style={{ margin: "1rem 0 0.5rem 0" }} />
                )}
                {renderChatList(
                  sharedChatList,
                  false,
                  true,
                  sharedChatList.length > 0 ? "Shared Chats" : undefined
                )}
              </>
            )}
            {sharedWithUserChats.length > 0 && (
              <>
                {sharedWithUserChats.length > 0 && (
                  <div style={{ margin: "1rem 0 0.5rem 0" }} />
                )}
                {renderChatList(
                  sharedWithUserChats,
                  false,
                  true,
                  sharedWithUserChats.length > 0 ? "Shared With Me" : undefined
                )}
              </>
            )}
          </div>
        )}
      </div>

      <hr
        style={{
          margin: "1rem 0",
          borderColor: currentColors.border,
        }}
      />
    </>
  );
};
